import { useState } from 'react';
import { useLoaderData, useRevalidator, useRouteLoaderData } from 'react-router';
import { api, errMsg, type SessionResponse } from '@/lib/api';
import { InviteUser } from './invite-user';

type User = { id: string; email: string; role: 'admin' | 'editor' };

export function usersLoader() {
  return api<User[]>('/auth/allowed-users');
}

export function UsersPage() {
  const users = useLoaderData<typeof usersLoader>();
  const session = useRouteLoaderData<SessionResponse>('app');
  const { revalidate } = useRevalidator();
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null);

  async function promote(user: User) {
    setBusy(user.id);
    setMessage(null);
    try {
      await api(`/auth/allowed-users/${user.id}/role`, {
        method: 'PATCH',
        json: { role: 'admin' },
      });
      await revalidate();
      setMessage({
        error: false,
        text: `${user.email} is now an admin. Their new role takes effect at their next sign-in or session refresh.`,
      });
    } catch (error) {
      setMessage({ error: true, text: errMsg(error, 'Could not update role') });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl">Users</h1>
        {session?.user.role === 'admin' && (
          <InviteUser onSent={(text) => setMessage({ error: false, text })} />
        )}
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>
                  {session?.user.role === 'admin' && user.role !== 'admin' ? (
                    <div className="flex items-center gap-2">
                      <select
                        aria-label={`Role for ${user.email}`}
                        value={user.role}
                        disabled={busy !== null}
                        onChange={(event) => {
                          if (event.target.value === 'admin') void promote(user);
                        }}
                        className="h-9 cursor-pointer rounded-md border px-2 disabled:cursor-wait"
                      >
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                      {busy === user.id && (
                        <span role="status" className="text-xs text-muted-foreground">
                          Updating…
                        </span>
                      )}
                    </div>
                  ) : (
                    <span>{user.role === 'admin' ? 'Admin' : 'Editor'}</span>
                  )}
                </td>
              </tr>
            ))}
            {!users.length && (
              <tr>
                <td colSpan={2}>No users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {message && (
        <p
          role={message.error ? 'alert' : 'status'}
          className={`rounded-md p-3 text-sm ${message.error ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800'}`}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}
